import type {
  AddonGroup,
  Invoice,
  InvoiceItem,
  PosPriceTier,
} from "@/lib/api/types";

/**
 * The client's mirror of the server's pricing rules.
 *
 * The authority is `apps/backend/src/modules/restaurant/policies/invoice-pricing.policy.ts`
 * and nothing here re-decides anything: the server prices every line, snapshots
 * the numbers onto the invoice item, and the till only ever renders what came
 * back. The two places this module exists for are the two where the screen has
 * to say a figure the server has not yet been asked for:
 *
 * - the ADDON PICKER, which quotes a line that does not exist yet, and
 * - the TOTALS BLOCK, which has to show what the discount was taken OFF.
 *
 * Both mirror the same two rules the policy carries:
 *
 * 1. **The tier discounts the ITEM, not the extras.** Addons are added at face
 *    value after the percentage is applied to the base price.
 * 2. **Round like the server.** Money is `numeric(_, 2)`; every derived figure
 *    goes through `roundMoney`, epsilon nudge included, so a quote and the line
 *    that lands a second later cannot differ by a piastre.
 */

/** `roundMoney` from the pricing policy, character for character. */
export function roundMoney(value: number): number {
  // Number.EPSILON nudge: 1.005 is really 1.00499… in binary floating point, so
  // a bare Math.round would round it DOWN and lose a piastre per line.
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/** The unit price actually charged, after the invoice's tier (0–100). */
export function resolveUnitChargedPrice(
  unitListPrice: number,
  discountPercent: number,
): number {
  return roundMoney(unitListPrice * (1 - discountPercent / 100));
}

/**
 * What one plate will cost: the tiered base plus its modifiers at face value.
 *
 * This is the client twin of `priceLine`'s `lineTotal`, and it is what the
 * picker's confirm button must say — quoting the menu's list price on a tiered
 * invoice promises a number the bill will never show.
 */
export function quoteLineTotal(
  unitListPrice: number,
  discountPercent: number,
  addonPrices: readonly number[],
  /**
   * The open swap's surcharge, when the waiter confirmed one before this quote
   * (D108). Zero for the ordinary plate, which is every plate that reaches
   * this function without passing through the swap dialog first.
   *
   * It sits BESIDE the addon prices rather than inside them even though the
   * server folds both into `addonsTotal`: the two arrive from different
   * dialogs, and a caller that had to concatenate them could silently pass
   * neither. The arithmetic is identical either way.
   */
  substitutionCharge = 0,
): number {
  const addonsTotal = roundMoney(
    addonPrices.reduce((sum, price) => sum + price, 0),
  );
  return roundMoney(
    resolveUnitChargedPrice(unitListPrice, discountPercent) +
      addonsTotal +
      substitutionCharge,
  );
}

/**
 * The subtotal a discount line is subtracted FROM.
 *
 * The server's `subtotal` is already NET of the tier (it sums `lineTotal`,
 * which is priced at the charged base), and `total` equals it. Rendering that
 * figure above a discount row states arithmetic that is false on its face —
 * `127 − 28 = 127`. What the discount was taken off is `subtotal +
 * discountTotal`, both of which the server sends, so this sums rather than
 * re-derives.
 */
export function listSubtotal(totals: {
  subtotal: number;
  discountTotal: number;
}): number {
  return roundMoney(totals.subtotal + totals.discountTotal);
}

/**
 * The invoice's charge snapshot (D33), as the pricing mirror consumes it.
 *
 * Null percent = that charge was OFF when the table was seated. The values
 * come off the INVOICE, never off the store's current settings — the same
 * snapshot rule the tier follows.
 */
export type InvoiceChargeRates = {
  serviceChargePercent: number | null;
  vatPercent: number | null;
  vatOnServiceCharge: boolean;
  pricesIncludeTax: boolean;
};

/**
 * Service charge + VAT on a chargeable subtotal — `applyInvoiceCharges` from
 * the pricing policy, character for character (D33–D35).
 *
 * Exclusive mode ADDS the lines on top (service on the subtotal; VAT on
 * subtotal + service per the D35 toggle). Inclusive mode keeps the total AT
 * the subtotal — the menu price is what the customer pays — and only
 * decomposes it for display, with the remainder landing on the VAT line so
 * the printed lines always sum to the total.
 */
export function applyInvoiceCharges(
  subtotal: number,
  charges: InvoiceChargeRates | null,
): { serviceChargeTotal: number; vatTotal: number; total: number } {
  const s = (charges?.serviceChargePercent ?? 0) / 100;
  const v = (charges?.vatPercent ?? 0) / 100;

  if (s === 0 && v === 0) {
    return { serviceChargeTotal: 0, vatTotal: 0, total: subtotal };
  }

  if (charges?.pricesIncludeTax) {
    const divisor = charges.vatOnServiceCharge
      ? (1 + s) * (1 + v)
      : 1 + s + v;
    const net = roundMoney(subtotal / divisor);
    // The rounding remainder lands on the LAST ENABLED charge, so the printed
    // lines always sum to the total — and a service-only store never shows a
    // VAT figure it did not charge. Mirrors the policy exactly.
    if (v === 0) {
      return {
        serviceChargeTotal: roundMoney(subtotal - net),
        vatTotal: 0,
        total: subtotal,
      };
    }
    const serviceChargeTotal = roundMoney(net * s);
    const vatTotal = roundMoney(subtotal - net - serviceChargeTotal);
    return { serviceChargeTotal, vatTotal, total: subtotal };
  }

  const serviceChargeTotal = roundMoney(subtotal * s);
  const vatBase = charges?.vatOnServiceCharge
    ? subtotal + serviceChargeTotal
    : subtotal;
  const vatTotal = roundMoney(vatBase * v);
  return {
    serviceChargeTotal,
    vatTotal,
    total: roundMoney(subtotal + serviceChargeTotal + vatTotal),
  };
}

/*
 * D146 — A DISCOUNT AT THE BILL («الخصم»), the client mirror of the pricing
 * policy's discount arithmetic (`netFoodOf`, `foodBaseAfterDiscount`,
 * `effectiveBillDiscount`, `priceWithBillDiscount`) and of the back-solve
 * (`bill-discount.policy.ts`), character for character. The server decides
 * every discount; the till only has to SAY a figure before it is asked — the
 * running total of an open bill, and the sheet's preview of the new total.
 */

/** What pricing needs to apply a stored discount (D146). */
export type BillDiscountPricing = {
  kind: "PERCENT" | "AMOUNT" | "TARGET_TOTAL";
  value: number;
  amount: number;
  capPct: number;
};

function inclusiveDivisor(charges: InvoiceChargeRates | null): number {
  const s = (charges?.serviceChargePercent ?? 0) / 100;
  const v = (charges?.vatPercent ?? 0) / 100;
  if (!charges?.pricesIncludeTax || (s === 0 && v === 0)) return 1;
  return charges.vatOnServiceCharge ? (1 + s) * (1 + v) : 1 + s + v;
}

/** The bill's NET food — the discount's base (derived in inclusive mode). */
export function netFoodOf(
  subtotal: number,
  charges: InvoiceChargeRates | null,
): number {
  const divisor = inclusiveDivisor(charges);
  return divisor === 1 ? subtotal : roundMoney(subtotal / divisor);
}

/** The food after a NET discount, in the subtotal's own basis. */
export function foodBaseAfterDiscount(
  subtotal: number,
  charges: InvoiceChargeRates | null,
  discountNet: number,
): number {
  if (!(discountNet > 0)) return subtotal;
  const divisor = inclusiveDivisor(charges);
  if (divisor === 1) return roundMoney(subtotal - discountNet);
  return roundMoney((netFoodOf(subtotal, charges) - discountNet) * divisor);
}

/**
 * The net a stored discount takes NOW: a PERCENT tracks the current net, an
 * AMOUNT/TARGET is fixed; both are clamped to the cap snapshotted at apply.
 */
export function effectiveBillDiscount(
  subtotal: number,
  charges: InvoiceChargeRates | null,
  discount: BillDiscountPricing | null | undefined,
): number {
  if (!discount) return 0;
  const net = netFoodOf(subtotal, charges);
  if (!(net > 0)) return 0;
  const wanted =
    discount.kind === "PERCENT"
      ? roundMoney((net * discount.value) / 100)
      : discount.amount;
  const cap = roundMoney((net * discount.capPct) / 100);
  return Math.max(0, Math.min(wanted, cap));
}

/** The food money with a NET discount off: the line, the charges, the total. */
export function priceWithBillDiscount(
  subtotal: number,
  charges: InvoiceChargeRates | null,
  discountNet: number,
): {
  billDiscountTotal: number;
  /** As it comes off the printed subtotal (= the net in exclusive mode). */
  billDiscountGross: number;
  serviceChargeTotal: number;
  vatTotal: number;
  total: number;
} {
  const base = foodBaseAfterDiscount(subtotal, charges, discountNet);
  const charged = applyInvoiceCharges(base, charges);
  return {
    billDiscountTotal: discountNet > 0 ? roundMoney(discountNet) : 0,
    billDiscountGross: roundMoney(subtotal - base),
    ...charged,
  };
}

/** A bill's stored discount, in the shape the pricing mirror takes. */
export function billDiscountPricingOf(invoice: {
  billDiscount?: Invoice["billDiscount"];
}): BillDiscountPricing | null {
  const discount = invoice.billDiscount;
  if (!discount) return null;
  return {
    kind: discount.kind,
    value: discount.value,
    amount: discount.amount,
    capPct: discount.capPct,
  };
}

/**
 * «خليها» — the NET discount whose payable is closest to the target (ties to
 * the higher payable), the server's back-solve mirrored for the preview.
 * `feeTotal` is the delivery fee and its VAT, inside the payable and never
 * discounted.
 */
export function backSolveTargetTotal(input: {
  subtotal: number;
  charges: InvoiceChargeRates | null;
  feeTotal: number;
  target: number;
}): number {
  const payable = (discount: number) =>
    roundMoney(
      priceWithBillDiscount(input.subtotal, input.charges, discount).total +
        input.feeTotal,
    );
  const net = netFoodOf(input.subtotal, input.charges);
  const netPiastres = Math.round(net * 100);
  if (netPiastres <= 0 || payable(0) <= input.target) return 0;
  let lo = 0;
  let hi = netPiastres;
  if (payable(hi / 100) >= input.target) return net;
  while (hi - lo > 1) {
    const mid = Math.floor((lo + hi) / 2);
    if (payable(mid / 100) >= input.target) lo = mid;
    else hi = mid;
  }
  const above = Math.round((payable(lo / 100) - input.target) * 100);
  const below = Math.round((input.target - payable(hi / 100)) * 100);
  return (below < above ? hi : lo) / 100;
}

/** Why the sheet cannot send a discount yet — the server's own refusals. */
export type BillDiscountPreviewProblem =
  | "NOTHING_OFF"
  | "WHOLE_BILL"
  | "OVER_CAP"
  | "BELOW_PAID";

/**
 * The sheet's PREVIEW of a discount before it is sent (D146): the net it
 * resolves to, the bill's new payable, and the first of the server's refusals
 * it would meet — in the server's order — so the confirm can hold rather than
 * send a request it knows will be refused. The server re-decides every one.
 */
export function previewBillDiscount(input: {
  kind: BillDiscountPricing["kind"];
  value: number;
  /** The bill's chargeable food as it stands (live). */
  subtotal: number;
  charges: InvoiceChargeRates | null;
  feeTotal: number;
  capPct: number;
  tendered: number;
}): {
  net: number;
  totalAfter: number;
  maxAmount: number;
  problem: BillDiscountPreviewProblem | null;
} {
  const net = netFoodOf(input.subtotal, input.charges);
  const maxAmount = roundMoney((net * input.capPct) / 100);
  const wanted = !(input.value > 0)
    ? 0
    : input.kind === "PERCENT"
      ? roundMoney((net * input.value) / 100)
      : input.kind === "AMOUNT"
        ? roundMoney(input.value)
        : backSolveTargetTotal({
            subtotal: input.subtotal,
            charges: input.charges,
            feeTotal: input.feeTotal,
            target: roundMoney(input.value),
          });
  const totalAfter = roundMoney(
    priceWithBillDiscount(input.subtotal, input.charges, wanted).total +
      input.feeTotal,
  );
  const problem: BillDiscountPreviewProblem | null =
    !(net > 0) || !(wanted > 0)
      ? "NOTHING_OFF"
      : wanted >= net
        ? "WHOLE_BILL"
        : wanted > maxAmount
          ? "OVER_CAP"
          : roundMoney(input.tendered) > totalAfter
            ? "BELOW_PAID"
            : null;
  return { net: wanted, totalAfter, maxAmount, problem };
}

/**
 * The goods value before charges — DEF-6.
 *
 * Inclusive-mode receipts print each printed price with the charges already
 * folded in, so the customer-facing total never shows what was actually the
 * bill for FOOD versus the charges added on top of it. `total −
 * serviceChargeTotal − vatTotal` recovers that figure without re-deriving it:
 * it is deliberately NOT "the VAT base" — when `vatOnServiceCharge` is on,
 * VAT is computed on net + service charge, not on net alone, so a "taxable
 * amount" label on this number would contradict the VAT line beside it.
 */
export function netBeforeCharges(totals: {
  total: number;
  serviceChargeTotal: number;
  vatTotal: number;
}): number {
  return roundMoney(
    totals.total - totals.serviceChargeTotal - totals.vatTotal,
  );
}

/** The invoice's snapshot, in the shape `applyInvoiceCharges` takes. */
export function invoiceChargeRates(
  invoice: Pick<
    Invoice,
    | "serviceChargePercent"
    | "vatPercent"
    | "vatOnServiceCharge"
    | "pricesIncludeTax"
  >,
): InvoiceChargeRates {
  return {
    serviceChargePercent: invoice.serviceChargePercent,
    vatPercent: invoice.vatPercent,
    vatOnServiceCharge: invoice.vatOnServiceCharge,
    pricesIncludeTax: invoice.pricesIncludeTax,
  };
}

/**
 * The tier percentage this invoice was opened on.
 *
 * The invoice carries only `priceTierId` — the percentage lives on the tier
 * list the till already fetches (`GET /restaurant/invoices/price-tiers`). When
 * that read has not landed (or the tier was deactivated since the bill was
 * opened, so it is no longer in the active list), the percentage is DERIVED
 * from a line the server already priced: `unitChargedPrice / unitListPrice` is
 * exactly the concession that was applied, snapshotted at open. Only lines with
 * a list price above zero can answer, and a bill with no such line yet has
 * nothing to derive from — which is also the only case where the picker's quote
 * can be nothing but the list price.
 */
export function invoiceDiscountPercent(
  invoice: Pick<Invoice, "priceTierId"> & {
    items: readonly Pick<InvoiceItem, "unitListPrice" | "unitChargedPrice">[];
  },
  tiers: readonly PosPriceTier[],
): number {
  if (!invoice.priceTierId) return 0;

  const tier = tiers.find((entry) => entry.id === invoice.priceTierId);
  if (tier) return tier.discountPercent;

  const priced = invoice.items.find((item) => item.unitListPrice > 0);
  if (!priced) return 0;

  return (1 - priced.unitChargedPrice / priced.unitListPrice) * 100;
}

/**
 * The groups to hand a picker for ONE menu item, with this dish's own prices.
 *
 * An addon's `price` is the CATALOGUE default; a menu item may charge something
 * else for it, or not offer it at all. The server resolves both when it prices
 * a line, so a client that showed the catalogue figure would quote one number
 * and bill another — the exact mismatch the tier-aware quote already exists to
 * prevent.
 *
 * Resolving HERE, once, is deliberate: the picker takes groups already resolved
 * and stays ignorant of overrides, so there is one place this rule can be got
 * wrong rather than one per surface.
 *
 * A group left with no options is dropped. The server already omits such a
 * group from `addonGroupIds`, so this is the belt to that braces — the picker
 * treats an empty group list as a failed read and blocks confirm, and an
 * option-less group inside a non-empty list would render as a heading with
 * nothing under it.
 */
export function resolveGroupsForItem(
  item: {
    addonGroupIds: readonly string[];
    addonOverrides: readonly {
      addonId: string;
      price: number | null;
      isAvailable: boolean;
    }[];
  },
  groupsById: ReadonlyMap<string, AddonGroup>,
): AddonGroup[] {
  const overrideByAddonId = new Map(
    item.addonOverrides.map((override) => [override.addonId, override]),
  );

  return item.addonGroupIds
    .map((groupId) => groupsById.get(groupId))
    .filter((group): group is AddonGroup => Boolean(group))
    .map((group) => ({
      ...group,
      addons: group.addons
        // Paused in the catalogue, or not offered by this dish: both mean the
        // option is not on tonight. `isActive` is filtered HERE as well as in
        // the picker so the two cannot disagree about whether a group still has
        // anything in it — the caller decides whether to open a picker at all
        // from this list.
        .filter((addon) => addon.isActive)
        .filter(
          (addon) => overrideByAddonId.get(addon.id)?.isAvailable !== false,
        )
        .map((addon) => {
          const override = overrideByAddonId.get(addon.id);
          // An explicit `!== null` test and not a falsy one: a 0 override is
          // "free on this dish", a real decision, and falsiness would silently
          // charge the catalogue price.
          return override && override.price !== null
            ? { ...addon, price: override.price }
            : addon;
        }),
    }))
    .filter((group) => group.addons.length > 0);
}

/**
 * What the TABLE-SIDE HAND-OFF should do with a dish the waiter just tapped:
 * open its picker, add it as it is, or wait because the answer is not knowable
 * yet.
 *
 * **`WAIT` is the whole point of this function.** `InvoicePage` used to decide
 * this synchronously at mount by reading the addon-group cache — and
 * `TablePage`, the only screen that hands a dish over, did not populate that
 * key until B108. So on the first table-side order of a session the groups were simply
 * absent, "absent" was read as "this dish has no addons", and the dish was
 * staged with no selections. The server refused it, correctly, and the
 * waiter's first order of any dish with a required group failed and had to be
 * repeated. Absent and none-of-them are different answers, and only one of
 * them is safe to act on.
 *
 * `groups` is `undefined` while the query is in flight (or has failed) — the
 * caller keeps the hand-off pending rather than dropping the dish, so a
 * successful retry still lands the waiter's tap.
 *
 * **BOTH SCREENS ASK IT NOW (B108).** `TablePage` calls it on the tap itself,
 * before any command goes out: the picker runs there and the bill is opened
 * only once the waiter confirms it, because opening first meant a cancelled
 * picker left a 0 EGP invoice holding the table. `WAIT` is what that screen
 * renders its loader on — a waiter who has already tapped needs something
 * holding their place, or the tap reads as dead and gets repeated. `InvoicePage`
 * still asks it for a dish tapped on its own menu and for a hand-off from a
 * build that predates the add-on ids.
 *
 * `ADD` also covers a dish whose groups are all currently empty: the picker
 * treats an empty group list as a failed read and blocks confirm, so opening
 * one there would strand the waiter on data that is perfectly fine.
 */
export type HandoffDecision = "PICKER" | "ADD" | "WAIT";

export function handoffDecision(
  /** The dish the waiter tapped, or `null` when there is no hand-off at all. */
  productId: string | null,
  /**
   * That dish, resolved against the menu — or `undefined` while the MENU
   * itself is still loading. `null` means the menu has loaded and does not
   * carry it.
   *
   * The menu is a second cache with the same trap as the groups: read at mount
   * it is empty, an unresolved dish looked like a dish with no addons, and it
   * was added blind. Both caches have to have answered before this can decide.
   */
  item: Parameters<typeof resolveGroupsForItem>[0] | null | undefined,
  groups: readonly AddonGroup[] | undefined,
): HandoffDecision {
  if (!productId) return "ADD";
  if (item === undefined || groups === undefined) return "WAIT";
  // The menu HAS loaded and does not carry this dish — 86'd or deleted between
  // the table view and here. Nothing to open a picker over; let the add go and
  // let the server give its own answer, which is the honest one.
  if (item === null) return "ADD";
  return resolveGroupsForItem(item, new Map(groups.map((g) => [g.id, g])))
    .length > 0
    ? "PICKER"
    : "ADD";
}

/**
 * PAPER — the "from" price on a register tile (2026-10-03): the least this
 * dish can be added at, for a dish that asks for a size or extras first.
 *
 * The tiered base, plus the cheapest way to satisfy each REQUIRED group —
 * its cheapest option, taken `minSelect` times where repeats are allowed (the
 * picker counts total picks), once where the group is a single choice.
 * Optional groups add nothing: "from" is the floor, not a guess at an order.
 * `groups` are the dish's RESOLVED groups (`resolveGroupsForItem`), so an
 * option's per-dish price and an option the dish does not offer are already
 * accounted for. `null` for a dish with no groups — its tile shows its price.
 */
export function quoteFromPrice(
  unitListPrice: number,
  discountPercent: number,
  groups: readonly {
    minSelect: number;
    maxSelect: number | null;
    addons: readonly { price: number; isActive: boolean }[];
  }[],
): number | null {
  if (groups.length === 0) return null;
  const required: number[] = [];
  for (const group of groups) {
    if (group.minSelect <= 0) continue;
    const cheapest = Math.min(
      ...group.addons.filter((addon) => addon.isActive).map((addon) => addon.price),
    );
    if (!Number.isFinite(cheapest)) continue;
    const picks = group.maxSelect === 1 ? 1 : group.minSelect;
    for (let pick = 0; pick < picks; pick += 1) required.push(cheapest);
  }
  return quoteLineTotal(unitListPrice, discountPercent, required);
}
